import * as z from 'zod';

import type * as api from '@gitbook/api-client';
import { ensureIsDefined } from '@gitbook/util-ts';
import { isUrl } from '@gitbook/util-url';

import { CONTENT_REF_KINDS, type ContentRefKind } from './types';

export const ContentReferenceSchemas: {
    [K in ContentRefKind]: z.ZodType<Extract<api.ContentRef, { kind: K }>>;
} = {
    user: z.object({
        kind: z.literal('user'),
        user: z.string(),
    }) satisfies z.ZodType<api.ContentRefUser>,
    collection: z.object({
        kind: z.literal('collection'),
        collection: z.string(),
    }) satisfies z.ZodType<api.ContentRefCollection>,
    space: z.object({
        kind: z.literal('space'),
        space: z.string(),
    }) satisfies z.ZodType<api.ContentRefSpace>,
    page: z.object({
        kind: z.literal('page'),
        space: z.string().optional(),
        page: z.string(),
    }) satisfies z.ZodType<api.ContentRefPage>,
    anchor: z.object({
        kind: z.literal('anchor'),
        space: z.string().optional(),
        page: z.string().optional(),
        anchor: z.string(),
    }) satisfies z.ZodType<api.ContentRefAnchor>,
    file: z.object({
        kind: z.literal('file'),
        file: z.string(),
    }) satisfies z.ZodType<api.ContentRefFile>,
    url: z.object({
        kind: z.literal('url'),
        url: z.string().refine(isUrl, {
            error: 'Invalid URL',
        }),
    }) satisfies z.ZodType<api.ContentRefURL>,
    'reusable-content': z.object({
        kind: z.literal('reusable-content'),
        reusableContent: z.string(),
        space: z.string().optional(),
    }) satisfies z.ZodType<api.ContentRefReusableContent>,
    tag: z.object({
        kind: z.literal('tag'),
        tag: z.string(),
        space: z.string().optional(),
    }) satisfies z.ZodType<api.ContentRefTag>,
    openapi: z.object({
        kind: z.literal('openapi'),
        spec: z.string(),
    }) satisfies z.ZodType<api.ContentRefOpenAPI>,
};

export const ContentReferenceSchema = z.union([
    ContentReferenceSchemas.user,
    ContentReferenceSchemas.collection,
    ContentReferenceSchemas.space,
    ContentReferenceSchemas.page,
    ContentReferenceSchemas.anchor,
    ContentReferenceSchemas.file,
    ContentReferenceSchemas.url,
    ContentReferenceSchemas['reusable-content'],
    ContentReferenceSchemas.tag,
    ContentReferenceSchemas.openapi,
]) satisfies z.ZodType<api.ContentRef>;

type KindToContentRef = {
    [C in api.ContentRef as C['kind']]: C;
};

/**
 * Validate a content reference.
 */
export function validateContentReference<T extends ContentRefKind[]>(kinds: T) {
    const matches: ReadonlyArray<z.ZodType<api.ContentRef>> = kinds.map((kind) =>
        ensureIsDefined(
            ContentReferenceSchemas[kind],
            `No schema found for content reference kind: ${kind}`
        )
    );

    const schema = (() => {
        if (matches.length === 0) {
            return z.never();
        }
        if (matches.length === 1) {
            return matches[0];
        }

        // just to get zod typing to play well
        return z.union([matches[0], matches[1], ...matches.slice(2)]);
    })();

    return (data: unknown): data is KindToContentRef[T[number]] => schema.validate(data);
}

/**
 * Validate any content reference.
 *
 * This is a convenience function that validates against all known content reference kinds.
 */
export const validateAnyContentReference = validateContentReference(CONTENT_REF_KINDS);
